 
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Box,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import { ScoringPoints } from './HousingRuleInfo';

 
  // --- Begin FantasyScoringTable and helpers ---
  const humanize = (s = "") =>
    String(s)
      .replace(/_/g, " ")
      .replace(/\bper\b/gi, "(per)")
      .replace(/\s+per\s+/gi, " (per ")
      .replace(/\s*-\s*/g, " — ")
      .replace(/\s+yard(s)?/i, " yard$1")
      .replace(/\bfg\b/gi, "FG")
      .replace(/\bpat\b/gi, "PAT")
      .replace(/\bpts?\b/gi, "Points")
      .replace(/\b(\w)/g, (m) => m.toUpperCase())
      .trim();

  const flattenScoringToText = (sportName, scoringObj) => {
    const lines = [`${sportName} Fantasy Scoring:`];
    Object.entries(scoringObj || {}).forEach(([section, values]) => {
      lines.push(`- ${section}:`);
      if (Array.isArray(values)) {
        values.forEach((row) => {
          if (row.range !== undefined && row.points !== undefined) {
            lines.push(`  • ${row.range} — ${row.points}`);
          } else {
            lines.push(`  • ${JSON.stringify(row)}`);
          }
        });
      } else if (values && typeof values === "object") {
        Object.entries(values).forEach(([k, v]) => {
          lines.push(`  • ${humanize(k)} — ${v}`);
        });
      }
    });
    return lines.join("\n");
  };

  // Renders rows for any scoring data shape:
  // - Plain object of { stat: points }
  // - Array of { range, points } (or { stat, pts })
  // - Nested arrays on object values (e.g., "Points Allowed": [ ... ])
  const renderSectionRows = (data, humanizeFn) => {
    const rows = [];
    if (Array.isArray(data)) {
      data.forEach((row, i) => {
        rows.push(
          <TableRow key={`arr-${i}`}>
            <TableCell>{row.range ?? humanizeFn(row.stat ?? "Range")}</TableCell>
            <TableCell align="right">{row.points ?? row.pts ?? ""}</TableCell>
          </TableRow>
        );
      });
      return rows;
    }
    if (data && typeof data === "object") {
      Object.entries(data).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          // Subheader row for this group, followed by its array rows
          rows.push(
            <TableRow key={`subhead-${key}`}>
              <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontWeight: 700 }}>
                {humanizeFn(key)}
              </TableCell>
            </TableRow>
          );
          val.forEach((row, i) => {
            rows.push(
              <TableRow key={`sub-${key}-${i}`}>
                <TableCell>{row.range ?? humanizeFn(row.stat ?? "Range")}</TableCell>
                <TableCell align="right">{row.points ?? row.pts ?? ""}</TableCell>
              </TableRow>
            );
          });
        } else if (val && typeof val === "object") {
          // Rare case: nested object – render a subheader and then its leaves
          rows.push(
            <TableRow key={`subobj-${key}`}>
              <TableCell colSpan={2} sx={{ bgcolor: "action.hover", fontWeight: 700 }}>
                {humanizeFn(key)}
              </TableCell>
            </TableRow>
          );
          Object.entries(val).forEach(([k2, v2]) => {
            rows.push(
              <TableRow key={`subobj-${key}-${k2}`}>
                <TableCell>{humanizeFn(k2)}</TableCell>
                <TableCell align="right">{v2}</TableCell>
              </TableRow>
            );
          });
        } else {
          rows.push(
            <TableRow key={key}>
              <TableCell>{humanizeFn(key)}</TableCell>
              <TableCell align="right">{val}</TableCell>
            </TableRow>
          );
        }
      });
      return rows;
    }
    // Fallback: nothing to render
    return rows;
  };

 export function FantasyScoringTable({ sportName }) {
    const scoring = ScoringPoints?.[sportName];
    if (!scoring) {
      return (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              House Rules — {sportName || "Unknown Sport"}
            </Typography>
            <Tooltip title="No fantasy scoring configured for this sport yet" arrow>
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          </Stack>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              We couldn't find fantasy point rules for <strong>{sportName || "this sport"}</strong>.
            </Typography>
          </Paper>
        </Box>
      );
    }

    const sections = Object.entries(scoring);

    return (
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            House Rules — {sportName} (Scoring)
          </Typography>
          <Tooltip title={`How fantasy points are calculated for ${sportName}`} arrow>
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
          {/* <Tooltip title="Copy these rules" arrow>
            <IconButton
              size="small"
              onClick={() => {
                const txt = flattenScoringToText(sportName, scoring);
                if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(txt);
              }}
              aria-label="copy fantasy scoring rules"
            >
              <ContentCopyOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip> */}
        </Stack>

        <Stack spacing={2}>
          {sections.map(([sectionName, data]) => (
            <Box key={sectionName}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 0.5, fontWeight: 700, color: "text.secondary" }}
              >
                {sectionName}
              </Typography>
              <TableContainer sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title={`${sportName} stat category`} arrow>
                            <span>Stat</span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title="Fantasy points awarded (negative when applicable)" arrow>
                            <span>Fantasy Points</span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renderSectionRows(data, humanize)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }